import { GetServerSideProps, NextPage } from 'next'
import nextI18nextConfig from '~/next-i18next.config'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { GetLatestTrophies } from '~/modules/Organizers/Application/GetLatestTrophies'
import { DependencyInjector } from '~/injector/dependency.injector'
import { GetFeaturedTrophy } from '~/modules/Trophies/Application/GetFeaturedTrophy/GetFeaturedTrophy'
import { ApiTrophyResponseInterface } from '~/modules/Trophies/Application/ApiTrophyResponseInterface'
import { HtmlPageMetaContextProps } from '~/components/HtmlPageMeta/HtmlPageMetaContextProps'
import { HtmlPageMetaContextService } from '~/components/HtmlPageMeta/HtmlPageMetaContextService'
import { GetOrganizerResponseInterface }
  from '~/modules/Organizers/Application/GetOrganizer/GetOrganizerResponseInterface'
import { TrophyRepositoryInterface } from '~/modules/Trophies/Domain/TrophyRepositoryInterface'
import { OrganizerRepositoryInterface } from '~/modules/Organizers/Domain/OrganizerRepositoryInterface'
import { GetOrganizer } from '~/modules/Organizers/Application/GetOrganizer/GetOrganizer'
import { ApplicationTrophyDto } from '~/modules/Products/Application/Dtos/ApplicationTrophyDto'
import { MarketOrganizerPage } from '~/components/MarketPages/OrganizerPage'
import { fromOrganizerPagePropsToViewModel } from '~/modules/PresentationAdapters/OrganizerPageAdapter'

export interface OrganizerPageProps {
  htmlPageMetaContextProps: HtmlPageMetaContextProps
  trophies: ApplicationTrophyDto[]
  featuredTrophy: ApiTrophyResponseInterface | null
  organizerResponse: GetOrganizerResponseInterface
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const locale = context.locale ? context.locale : nextI18nextConfig.i18n.defaultLocale
  const translationsPromise = serverSideTranslations(locale, [
    'user_tos',
    'app_menu',
    'footer',
    'faq',
    'latest_trophies',
    'trophies',
    'organizer',
    'organizer_header',
    'featured_trophy',
    'wallet_error_modal',
  ])

  const organizerId = context.query.organizerId as string
  const trophyRepository: TrophyRepositoryInterface = DependencyInjector.getInstance()
    .resolve('TrophyRepositoryInterface')
  const organizerRepository: OrganizerRepositoryInterface = DependencyInjector.getInstance()
    .resolve('OrganizerRepositoryInterface')
  let featuredTrophyPromise: Promise<ApiTrophyResponseInterface | null> = Promise.resolve(null)

  if (organizerId) {
    featuredTrophyPromise = (new GetFeaturedTrophy(trophyRepository))
      .run(organizerId)
      .catch((exception: unknown) => {
        console.error(exception)

        return null
      })
  }

  try {
    const [organizerResponse, trophies, featuredTrophy] = await Promise.all([
      (new GetOrganizer(organizerRepository, trophyRepository)).get(organizerId),
      (new GetLatestTrophies(organizerRepository, trophyRepository)).get(organizerId),
      featuredTrophyPromise,
    ])
    const htmlPageMetaContextService = new HtmlPageMetaContextService(context)

    return {
      props: {
        featuredTrophy: JSON.parse(JSON.stringify(featuredTrophy)),
        organizerResponse,
        trophies: JSON.parse(JSON.stringify(trophies)),
        htmlPageMetaContextProps: htmlPageMetaContextService.getProperties(),
        ...await translationsPromise,
      },
    }
  } catch (exception: unknown) {
    // Log error, we don't want to throw exceptions to our own frontend
    console.error(exception)

    return {
      notFound: true,
    }
  }
}

const OrganizerPage: NextPage<OrganizerPageProps> = (props) => {
  return <MarketOrganizerPage viewModel={ fromOrganizerPagePropsToViewModel(props) } />
}

export default OrganizerPage
